import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../api/client.js';
import { CloseIcon, MicIcon, SparkleIcon, StopIcon } from './icons.jsx';

/**
 * Boton flotante de IA: dicta una idea, transcribe + extrae campos y abre
 * la pagina de creacion con todo prerellenado para que el usuario pueda
 * revisar/ajustar antes de generar el cuento.
 *
 * Solo se muestra si hay token de sesion.
 */
export default function VoiceDictateFAB() {
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(!!getToken());

  useEffect(() => {
    const sync = () => setAuthed(!!getToken());
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('stories:token-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('stories:token-changed', sync);
    };
  }, []);

  if (!authed) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Crear cuento dictando"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white shadow-lg ring-4 ring-purple-200/70 transition hover:scale-105 hover:shadow-xl sm:bottom-7 sm:right-7 sm:h-16 sm:w-16"
        title="Dictar cuento por voz"
      >
        <span className="relative flex h-full w-full items-center justify-center">
          <MicIcon size="1.6em" />
          <SparkleIcon
            size="0.9em"
            className="absolute -right-0 -top-0 text-yellow-300"
          />
        </span>
      </button>
      {open && <DictateModal onClose={() => setOpen(false)} />}
    </>
  );
}

const STATES = {
  IDLE: 'idle',
  RECORDING: 'recording',
  PROCESSING: 'processing',
  ERROR: 'error',
};

// Tope de grabacion. Gemini transcribe audios largos sin problema, pero
// limitamos a 5 minutos para evitar grabaciones accidentales gigantes y
// mantener el request dentro del limite inline (20 MB).
const MAX_RECORD_SECONDS = 5 * 60;

function DictateModal({ onClose }) {
  const navigate = useNavigate();
  const [state, setState] = useState(STATES.IDLE);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTimeRef = useRef(0);
  const timerRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  function pickMimeType() {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    for (const m of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
    }
    return '';
  }

  async function startRecording() {
    setError(null);
    audioChunksRef.current = [];
    setElapsed(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mimeType || 'audio/webm',
        });
        const duration = (Date.now() - startTimeRef.current) / 1000;
        await sendForTranscription(blob, mimeType || 'audio/webm', duration);
      };

      startTimeRef.current = Date.now();
      recorder.start();
      setState(STATES.RECORDING);
      timerRef.current = setInterval(() => {
        const secs = (Date.now() - startTimeRef.current) / 1000;
        setElapsed(secs);
        // Auto-parada al alcanzar el tope de 5 minutos.
        if (secs >= MAX_RECORD_SECONDS) stopRecording();
      }, 200);
    } catch (err) {
      setError(
        err?.name === 'NotAllowedError'
          ? 'No se pudo acceder al microfono. Acepta el permiso e intentalo de nuevo.'
          : `No se pudo iniciar la grabacion: ${err.message}`
      );
      setState(STATES.ERROR);
    }
  }

  function stopRecording() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setState(STATES.PROCESSING);
  }

  async function sendForTranscription(blob, mimeType, durationSeconds) {
    try {
      const data = await api.voice.dictate({
        audioBlob: blob,
        mimeType,
        languageCode: 'es-neutro',
        durationSeconds,
      });
      // Cerramos el modal y abrimos /create con todo prerellenado para que el
      // usuario pueda revisar y editar antes de generar el cuento.
      cleanup();
      onClose();
      navigate('/create', {
        state: {
          prefill: data.fields,
          voiceCosts: data.costs ?? [],
          transcript: data.transcript,
        },
      });
    } catch (err) {
      setError(err.message ?? 'Error procesando el audio.');
      setState(STATES.ERROR);
    }
  }

  function resetForRetry() {
    setError(null);
    setElapsed(0);
    setState(STATES.IDLE);
  }

  function handleClose() {
    cleanup();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/50 p-3 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-purple-100 bg-gradient-to-r from-fuchsia-50 to-purple-50 px-5 py-3">
          <div className="flex items-center gap-2 text-purple-800">
            <MicIcon size="1.3em" />
            <h2 className="text-base font-bold sm:text-lg">Crear cuento dictando</h2>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={handleClose}
            className="rounded-full p-1.5 text-gray-500 hover:bg-purple-100 hover:text-purple-700"
          >
            <CloseIcon size="1.2em" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-5">
          {state === STATES.IDLE && <Idle onStart={startRecording} />}
          {state === STATES.RECORDING && (
            <Recording elapsed={elapsed} onStop={stopRecording} />
          )}
          {state === STATES.PROCESSING && (
            <Spinner label="Transcribiendo e interpretando lo que has dicho. Si has grabado mucho audio, esto puede tardar un poco..." />
          )}
          {state === STATES.ERROR && <ErrorView message={error} onRetry={resetForRetry} />}
        </div>
      </div>
    </div>
  );
}

function Idle({ onStart }) {
  return (
    <div className="space-y-3 text-center">
      <p className="text-sm text-gray-600">
        Pulsa el microfono y describe el cuento que quieres: idea, personaje, edad del publico,
        final, idioma... La IA rellenara el formulario y podras revisarlo antes de generar.
      </p>
      <button
        type="button"
        onClick={onStart}
        className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white shadow-lg ring-4 ring-purple-200 transition hover:scale-105"
      >
        <MicIcon size="2em" />
      </button>
      <p className="text-xs text-gray-500">Necesitamos permiso para el microfono.</p>
    </div>
  );
}

function Recording({ elapsed, onStop }) {
  const nearLimit = elapsed >= MAX_RECORD_SECONDS - 30;
  return (
    <div className="space-y-3 text-center">
      <p className="text-sm font-semibold text-red-600">
        Grabando... {formatSeconds(elapsed)} / {formatSeconds(MAX_RECORD_SECONDS)}
      </p>
      <button
        type="button"
        onClick={onStop}
        className="mx-auto flex h-20 w-20 animate-pulse items-center justify-center rounded-full bg-red-600 text-white shadow-lg ring-4 ring-red-200 transition hover:bg-red-700"
        aria-label="Parar grabacion"
      >
        <StopIcon size="2em" />
      </button>
      <p className="text-xs text-gray-500">
        {nearLimit
          ? 'Estas cerca del limite de 5 minutos; la grabacion se parara sola.'
          : 'Habla con claridad y con todo el detalle que quieras (hasta 5 minutos). Al terminar, pulsa para parar.'}
      </p>
    </div>
  );
}

function Spinner({ label }) {
  return (
    <div className="flex flex-col items-center gap-3 py-6">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600" />
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <div className="space-y-3">
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="w-full rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700"
      >
        Intentar de nuevo
      </button>
    </div>
  );
}

function formatSeconds(s) {
  const total = Math.floor(s);
  const m = Math.floor(total / 60).toString().padStart(1, '0');
  const sec = (total % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}
